import { useState } from "react";
import { Sparkles, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Textarea } from "../ui/textarea";

const TASKS = [
  ["quiz", "Comprehension quiz"], ["simplify", "Simplify story for age 3–7"], ["vocab", "Vocabulary suggestions"],
  ["distractors", "Distractor answers"], ["summary", "Lesson summary"], ["math", "Math mission questions"],
];

export const AIHelper = ({ stories }) => {
  const [story, setStory] = useState(stories[0]?.story_id || "");
  const [task, setTask] = useState("quiz");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState("");

  const run = async () => {
    setBusy(true);
    setOut("");
    try {
      const { data } = await api.post("/admin/ai/draft", { task, story_id: story, prompt });
      setOut(data.parsed ? JSON.stringify(data.parsed, null, 2) : data.raw);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally { setBusy(false); }
  };

  const saveDrafts = async () => {
    let items;
    try { items = JSON.parse(out); } catch { return toast.error("Edit the draft into valid JSON first"); }
    if (!Array.isArray(items)) return toast.error("Expected a list of questions");
    for (const [k, it] of items.entries()) {
      const opts = (it.options || []).map((o) => (typeof o === "string" ? { text: o, emoji: "" } : o));
      await api.post("/admin/c/activities", {
        lesson_id: story, activity_order: 100 + k, activity_type: "comprehension", instruction: it.question,
        question_data: { question: it.question, options: opts }, answer_options: opts.map((o) => o.text), correct_answer: it.correct,
        items: [], points: 10, attempt_limit: 3, difficulty: 1, status: "draft", subject_type: task === "math" ? "math" : "chinese", ai_generated: true,
      });
    }
    toast.success(`${items.length} draft activities saved — review & publish them in Activities`);
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-6" data-testid="ai-helper">
      <div className="scroll-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-bold"><Sparkles size={18} className="text-amber-600" /> AI content assistant</div>
        <p className="text-sm text-stone-500">Drafts are never published automatically. Edit, then save as drafts.</p>
        <select data-testid="ai-story-select" value={story} onChange={(e) => setStory(e.target.value)} className="w-full h-10 rounded-md border px-3 bg-white">
          {stories.map((s) => <option key={s.story_id} value={s.story_id}>{s.story_number}. {s.story_title}</option>)}
        </select>
        <select data-testid="ai-task-select" value={task} onChange={(e) => setTask(e.target.value)} className="w-full h-10 rounded-md border px-3 bg-white">
          {TASKS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Textarea data-testid="ai-prompt-input" rows={3} placeholder="Extra instructions (optional)" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <button data-testid="ai-generate-btn" disabled={busy} onClick={run} className="w-full h-11 rounded-full bg-[var(--ink)] text-white font-semibold disabled:opacity-50">{busy ? "Drafting…" : "Generate draft"}</button>
      </div>
      <div className="scroll-card p-6 space-y-3">
        <div className="font-bold">Draft (editable)</div>
        <Textarea data-testid="ai-output" rows={20} className="font-mono text-xs" value={out} onChange={(e) => setOut(e.target.value)} placeholder="AI output will appear here" />
        {(task === "quiz" || task === "math") && out && (
          <button data-testid="ai-save-drafts-btn" onClick={saveDrafts} className="h-10 px-5 rounded-full bg-[var(--vermilion)] text-white text-sm font-semibold flex items-center gap-2"><Save size={15} /> Save as draft activities</button>
        )}
      </div>
    </div>
  );
};
